// Task configuration: the fixed statuses, priorities and task types every
// project uses. These used to be per-project/organization database rows that
// an admin could edit (Settings -> Task Config); they are now constants so all
// projects behave the same and nothing can drift.
//
// A task stores the *key* (e.g. 'in_progress') in statusId / priorityId /
// typeId. Use the resolve* helpers to read anything that might still be in an
// older form (a display name, a legacy alias like 'highest'), and the
// present* helpers to turn a stored key into the object the API returns.
//
// This package has zero runtime dependencies, so it is safe for both the Node
// API and the browser-bundled web app.

export type TaskStatusCategory = 'TODO' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED'

export interface TaskStatusDef {
  key: string
  name: string
  color: string
  category: TaskStatusCategory
  position: number
  isCompleted: boolean
  isDefault: boolean
}

export interface TaskPriorityDef {
  key: string
  name: string
  label: string
  color: string
  position: number
}

export interface TaskTypeDef {
  key: string
  name: string
  description: string
  icon: string
  color: string
}

export const TASK_STATUSES: readonly TaskStatusDef[] = [
  { key: 'backlog', name: 'Backlog', color: '#94a3b8', category: 'TODO', position: 0, isCompleted: false, isDefault: true },
  { key: 'todo', name: 'To Do', color: '#64748b', category: 'TODO', position: 1, isCompleted: false, isDefault: false },
  { key: 'in_progress', name: 'In Progress', color: '#3b82f6', category: 'IN_PROGRESS', position: 2, isCompleted: false, isDefault: false },
  { key: 'in_review', name: 'In Review', color: '#f59e0b', category: 'IN_PROGRESS', position: 3, isCompleted: false, isDefault: false },
  { key: 'done', name: 'Done', color: '#22c55e', category: 'DONE', position: 4, isCompleted: true, isDefault: false },
]

export const TASK_PRIORITIES: readonly TaskPriorityDef[] = [
  { key: 'low', name: 'Low', label: 'Low', color: '#64748b', position: 0 },
  { key: 'medium', name: 'Medium', label: 'Medium', color: '#3b82f6', position: 1 },
  { key: 'high', name: 'High', label: 'High', color: '#f59e0b', position: 2 },
  { key: 'critical', name: 'Critical', label: 'Critical', color: '#ef4444', position: 3 },
]

export const TASK_TYPES: readonly TaskTypeDef[] = [
  { key: 'task', name: 'Task', description: 'Standard task', icon: 'check-square', color: '#6366f1' },
  { key: 'story', name: 'Story', description: 'A user-facing feature or requirement', icon: 'bookmark', color: '#22c55e' },
  { key: 'bug', name: 'Bug', description: 'A defect to fix', icon: 'bug', color: '#ef4444' },
  { key: 'epic', name: 'Epic', description: 'A large body of work spanning multiple stories', icon: 'zap', color: '#a855f7' },
  { key: 'subtask', name: 'Subtask', description: 'A smaller piece of a parent task', icon: 'corner-down-right', color: '#64748b' },
]

export type TaskStatusKey = 'backlog' | 'todo' | 'in_progress' | 'in_review' | 'done'
export type TaskPriorityKey = 'low' | 'medium' | 'high' | 'critical'
export type TaskTypeKey = 'task' | 'story' | 'bug' | 'epic' | 'subtask'

export const DEFAULT_STATUS_KEY: TaskStatusKey = 'backlog'
export const DEFAULT_PRIORITY_KEY: TaskPriorityKey = 'medium'
export const DEFAULT_TYPE_KEY: TaskTypeKey = 'task'

// 'To Do', 'to-do', 'TODO' and 'todo' all name the same thing.
const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')

// Other spellings that have been stored or sent over time. Keys are normalized.
const STATUS_ALIASES: Record<string, TaskStatusKey> = {
  open: 'todo',
  new: 'todo',
  inprogress: 'in_progress',
  doing: 'in_progress',
  review: 'in_review',
  inreview: 'in_review',
  completed: 'done',
  closed: 'done',
  resolved: 'done',
}

// The issue form used a five-level scale (highest..lowest) that the stored
// four levels never matched; the extremes fold into the nearest level.
const PRIORITY_ALIASES: Record<string, TaskPriorityKey> = {
  highest: 'critical',
  urgent: 'critical',
  blocker: 'critical',
  emergency: 'high',
  normal: 'medium',
  lowest: 'low',
  none: 'low',
  minor: 'low',
}

const TYPE_ALIASES: Record<string, TaskTypeKey> = {
  issue: 'task',
  feature: 'story',
  defect: 'bug',
  subtasks: 'subtask',
  subtask: 'subtask',
}

function lookup<T extends { key: string; name: string }>(
  list: readonly T[],
  aliases: Record<string, string>,
  input: unknown
): T | undefined {
  if (typeof input !== 'string' || input.trim() === '') return undefined
  const n = normalize(input)
  return (
    list.find((d) => normalize(d.key) === n || normalize(d.name) === n) ??
    list.find((d) => d.key === aliases[n])
  )
}

/** The status for a key, display name or known alias; undefined if it isn't one. */
export const resolveStatus = (input: unknown) => lookup(TASK_STATUSES, STATUS_ALIASES, input)
export const resolvePriority = (input: unknown) => lookup(TASK_PRIORITIES, PRIORITY_ALIASES, input)
export const resolveTaskType = (input: unknown) => lookup(TASK_TYPES, TYPE_ALIASES, input)

export const defaultStatus = (): TaskStatusDef => TASK_STATUSES.find((s) => s.key === DEFAULT_STATUS_KEY)!
export const defaultPriority = (): TaskPriorityDef => TASK_PRIORITIES.find((p) => p.key === DEFAULT_PRIORITY_KEY)!
export const defaultTaskType = (): TaskTypeDef => TASK_TYPES.find((t) => t.key === DEFAULT_TYPE_KEY)!

// ---- the shapes the API returns -----------------------------------------
// Same shapes the populated database rows had (`_id` and `id` are the key), so
// clients that already read status/priority/type objects keep working.

export interface PresentedStatus {
  _id: string
  id: string
  name: string
  color: string
  category: TaskStatusCategory
  position: number
  isCompleted: boolean
}

export interface PresentedPriority {
  _id: string
  id: string
  name: string
  label: string
  color: string
  position: number
}

export interface PresentedTaskType {
  _id: string
  id: string
  name: string
  description: string
  icon: string
  color: string
}

export const presentStatus = (d: TaskStatusDef): PresentedStatus => ({
  _id: d.key,
  id: d.key,
  name: d.name,
  color: d.color,
  category: d.category,
  position: d.position,
  isCompleted: d.isCompleted,
})

export const presentPriority = (d: TaskPriorityDef): PresentedPriority => ({
  _id: d.key,
  id: d.key,
  name: d.name,
  label: d.label,
  color: d.color,
  position: d.position,
})

export const presentTaskType = (d: TaskTypeDef): PresentedTaskType => ({
  _id: d.key,
  id: d.key,
  name: d.name,
  description: d.description,
  icon: d.icon,
  color: d.color,
})

/**
 * What a task's stored value means, as an API object. A value that isn't a
 * recognised key (data from before the migration) falls back to the default
 * rather than breaking the response.
 */
export const statusOf = (stored: unknown): PresentedStatus => presentStatus(resolveStatus(stored) ?? defaultStatus())
export const priorityOf = (stored: unknown): PresentedPriority =>
  presentPriority(resolvePriority(stored) ?? defaultPriority())
export const taskTypeOf = (stored: unknown): PresentedTaskType =>
  presentTaskType(resolveTaskType(stored) ?? defaultTaskType())

export const STATUSES_PRESENTED: readonly PresentedStatus[] = TASK_STATUSES.map(presentStatus)
export const PRIORITIES_PRESENTED: readonly PresentedPriority[] = TASK_PRIORITIES.map(presentPriority)
export const TASK_TYPES_PRESENTED: readonly PresentedTaskType[] = TASK_TYPES.map(presentTaskType)
